// components/POSRequestPage.jsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { loadPOSOrders, loadPOSItems, updatePOSOrderShipping, updatePOSOrderField, deletePOSOrder, invokePOSShippedEmail, shippedNotifyWarning } from "../services/posService";
import NewPOSModal from "./NewPOSModal";
import POSShippingModal from "./POSShippingModal";
import HomeButton from "../../../shared/components/HomeButton.jsx";
import "./POSRequestPage.css";

const STATUS_CONFIG = {
  "Pending":            { label: "Pending",            cls: "pos-status-pending"  },
  "Ordered from Vendor":{ label: "Ordered from Vendor",cls: "pos-status-ordered"  },
  "Shipped":            { label: "Shipped",            cls: "pos-status-shipped"  },
  "Delivered":          { label: "Delivered",          cls: "pos-status-delivered"},
};
const ALL_STATUSES = ["Pending", "Ordered from Vendor", "Shipped", "Delivered"];

function fmt(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function trackingUrl(carrier, tracking) {
  if (!tracking || tracking.length < 5) return null;
  if (/fedex/i.test(carrier)) return `https://www.fedex.com/fedextrack/?trknbr=${tracking}`;
  if (/ups/i.test(carrier))   return `https://www.ups.com/track?tracknum=${tracking}`;
  return null;
}

function totalItems(items) {
  return Object.values(items || {}).reduce((sum, qty) => sum + (qty || 0), 0);
}

export default function POSRequestPage({ user, userPermissions, onHome }) {
  const canEditShipping  = userPermissions?.has("manage_pos_shipping") ?? false;

  const [orders,         setOrders]         = useState([]);
  const [loadingData,    setLoadingData]    = useState(true);
  const [showNewModal,   setShowNewModal]   = useState(false);
  const [successToast,   setSuccessToast]   = useState(false);
  // Item 204: a shipped notification that never left must be visible here, not
  // only in a function log. Sticky (no auto-dismiss) — it needs acting on.
  const [notifyWarning,  setNotifyWarning]  = useState(null);
  const [shippingRow,    setShippingRow]    = useState(null);
  const [expandedId,     setExpandedId]     = useState(null);
  const [search,         setSearch]         = useState("");
  const [statusFilter,   setStatusFilter]   = useState("all");
  const [sortKey,        setSortKey]        = useState(null);
  const [sortDir,        setSortDir]        = useState("asc");
  const [confirmDeleteId,setConfirmDeleteId]= useState(null);
  const [deleting,       setDeleting]       = useState(false);
  const [editCell,       setEditCell]       = useState(null); // { rowId, field }
  const [editValue,      setEditValue]      = useState("");
  const [undoStack,      setUndoStack]      = useState([]);

  const [posItems, setPosItems] = useState([]);

  useEffect(() => {
    loadPOSOrders()
      .then(setOrders)
      .catch(err => console.error("Failed to load POS orders:", err))
      .finally(() => setLoadingData(false));

    // Catalog is loaded in parallel so the expanded-row breakdown can show
    // labels for any item_key (including ones added after this build shipped).
    loadPOSItems()
      .then(setPosItems)
      .catch(err => console.error("Failed to load POS items:", err));
  }, []);

  const itemLabelByKey = useMemo(
    () => Object.fromEntries(posItems.map(p => [p.item_key, p.name])),
    [posItems]
  );

  // Item 204. `pos-shipped-notify` answers 200 { sent:false, reason } on every
  // failure so a shipping update is never blocked by a mail problem — which
  // means the ONLY place that can tell the user is here.
  const showNotifyWarning = (result) => setNotifyWarning(shippedNotifyWarning(result));

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const handleDelete = async (id) => {
    setDeleting(true);
    try {
      await deletePOSOrder(id);
      setOrders(prev => prev.filter(r => r.id !== id));
      setExpandedId(null);
      setConfirmDeleteId(null);
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setDeleting(false);
    }
  };

  const startEdit  = (rowId, field, value, e) => { if (e) e.stopPropagation(); setEditCell({ rowId, field }); setEditValue(value ?? ""); };
  const cancelEdit = () => { setEditCell(null); setEditValue(""); };
  const commitEdit = async () => {
    if (!editCell) return;
    const { rowId, field } = editCell;
    const rawVal = editValue.trim() || null;
    const val = field === "shippingCost" ? (rawVal !== null && rawVal !== "" ? parseFloat(rawVal) : null) : rawVal;
    const prevRow = orders.find(r => r.id === rowId);
    const oldValue = prevRow?.[field] ?? null;
    setEditCell(null);
    setEditValue("");
    try {
      await updatePOSOrderField(rowId, field, val);
      const updatedRow = { ...prevRow, [field]: val };
      setOrders(prev => prev.map(r => r.id === rowId ? updatedRow : r));
      setUndoStack(prev => [...prev, { rowId, field, oldValue, newValue: val }]);
      // Fire shipped notification when status transitions to Shipped
      if (field === "status" && val === "Shipped" && prevRow?.status !== "Shipped") {
        invokePOSShippedEmail(updatedRow, {}, user?.mail)
          .then(showNotifyWarning)
          .catch(err => { console.error(err); setNotifyWarning("Marked Shipped, but the notification email was NOT sent."); });
      }
    } catch (err) { console.error("Update failed:", err.message); }
  };

  const handleUndo = async () => {
    if (undoStack.length === 0) return;
    const last = undoStack[undoStack.length - 1];
    const { rowId, field, oldValue } = last;
    try {
      await updatePOSOrderField(rowId, field, oldValue);
      setOrders(prev => prev.map(r => r.id === rowId ? { ...r, [field]: oldValue } : r));
      setUndoStack(prev => prev.slice(0, -1));
    } catch (err) { console.error("Undo failed:", err.message); }
  };

  const isEdit = (rowId, field) => editCell?.rowId === rowId && editCell?.field === field;

  const posInput = (rowId, field, type = "text") => (
    <input
      className="pos-inline-input"
      type={type}
      value={editValue}
      autoFocus
      onChange={e => setEditValue(e.target.value)}
      onBlur={commitEdit}
      onKeyDown={e => { if (e.key === "Enter") commitEdit(); if (e.key === "Escape") cancelEdit(); }}
      onClick={e => e.stopPropagation()}
    />
  );

  const posSelect = (rowId, field, options) => (
    <select
      className="pos-inline-input pos-inline-select"
      value={editValue}
      autoFocus
      onChange={e => setEditValue(e.target.value)}
      onBlur={commitEdit}
      onKeyDown={e => { if (e.key === "Escape") cancelEdit(); }}
      onClick={e => e.stopPropagation()}
    >
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  const posCell = (rowId, field, display, rawValue, type = "text", options = null) => {
    if (isEdit(rowId, field)) return options ? posSelect(rowId, field, options) : posInput(rowId, field, type);
    return canEditShipping
      ? <span className="pos-editable" onClick={e => startEdit(rowId, field, rawValue, e)}>{display}</span>
      : display;
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return (
      <svg className="pos-sort-icon pos-sort-idle" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"/><polyline points="5 12 12 5 19 12"/>
      </svg>
    );
    return sortDir === "asc" ? (
      <svg className="pos-sort-icon" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"/><polyline points="5 12 12 5 19 12"/>
      </svg>
    ) : (
      <svg className="pos-sort-icon" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"/><polyline points="5 12 12 19 19 12"/>
      </svg>
    );
  };

  const COLS = [
    { key: "__details",         label: "",              defaultW: 80  },
    { key: "orderNumber",       label: "Order #",       defaultW: 100 },
    { key: "status",            label: "Status",        defaultW: 115 },
    { key: "shipToCompany",     label: "Ship To",       defaultW: 170 },
    { key: "shipToContactName", label: "Contact",       defaultW: 150 },
    { key: "orderDate",         label: "Order Date",    defaultW: 110 },
    { key: "requestedShipDate", label: "Req. Ship",     defaultW: 110 },
    { key: "submitterName",     label: "Submitted By",  defaultW: 140 },
    { key: "__items",           label: "Total Items",   defaultW: 90  },
    { key: "shipDate",          label: "Ship Date",     defaultW: 110 },
    { key: "shippingCost",      label: "Shipping Cost", defaultW: 115 },
    { key: "__tracking",        label: "Carrier / Tracking", defaultW: 200 },
    { key: null,                label: "",              defaultW: canEditShipping ? 150 : 40 },
  ];

  const [colWidths, setColWidths] = useState(() =>
    Object.fromEntries(COLS.map(c => [c.key ?? "__actions", c.defaultW]))
  );

  const startResize = useCallback((e, key) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = colWidths[key ?? "__actions"];
    const onMove = (me) => {
      const newW = Math.max(40, startW + me.clientX - startX);
      setColWidths(prev => ({ ...prev, [key ?? "__actions"]: newW }));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [colWidths]);

  const counts = useMemo(() => {
    const c = { total: 0, Pending: 0, "Ordered from Vendor": 0, Shipped: 0, Delivered: 0 };
    orders.forEach(r => { c.total++; if (c[r.status] !== undefined) c[r.status]++; });
    return c;
  }, [orders]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const rows = orders.filter(r => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (q) {
        const hay = [r.orderNumber, r.shipToCompany, r.shipToContactName, r.submitterName, r.tracking].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    if (!sortKey || sortKey.startsWith("__")) return rows;
    return [...rows].sort((a, b) => {
      const av = (a[sortKey] || "").toString().toLowerCase();
      const bv = (b[sortKey] || "").toString().toLowerCase();
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [orders, search, statusFilter, sortKey, sortDir]);

  if (loadingData) {
    return (
      <div className="pos-page" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: "var(--muted)" }}>
          <div className="loading-spinner" style={{ margin: "0 auto 12px" }} />
          Loading POS orders…
        </div>
      </div>
    );
  }

  const totalW = Object.values(colWidths).reduce((a, b) => a + b, 0);

  return (
    <>
    <div className="pos-page">

      {/* ── Toolbar ── */}
      <div className="pos-toolbar">
        <div className="pos-toolbar-left">
          <HomeButton onClick={onHome} />
          <h2 className="pos-title">POS Requests</h2>
        </div>
        <div className="pos-toolbar-right">
          {canEditShipping && (
            <button
              className="pos-undo-btn"
              onClick={handleUndo}
              disabled={undoStack.length === 0}
              title="Undo last change"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 14L4 9l5-5"/><path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11"/>
              </svg>
              Undo
            </button>
          )}
          <button className="pos-new-btn" onClick={() => setShowNewModal(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            New POS Order
          </button>
        </div>
      </div>

      {/* ── Summary cards ── */}
      <div className="pos-summary">
        {[
          { label: "Total Orders",        value: counts.total,                   cls: "",               filterKey: "all"                 },
          { label: "Pending",             value: counts.Pending,                 cls: "pos-card-gray",  filterKey: "Pending"             },
          { label: "Ordered from Vendor", value: counts["Ordered from Vendor"],  cls: "pos-card-blue",  filterKey: "Ordered from Vendor" },
          { label: "Shipped",             value: counts.Shipped,                 cls: "pos-card-green", filterKey: "Shipped"             },
          { label: "Delivered",           value: counts.Delivered,               cls: "pos-card-amber", filterKey: "Delivered"           },
        ].map(c => (
          <button
            key={c.label}
            className={`pos-card ${c.cls}${statusFilter === c.filterKey ? " pos-card-active" : ""}`}
            onClick={() => setStatusFilter(c.filterKey)}
          >
            <div className="pos-card-value">{c.value}</div>
            <div className="pos-card-label">{c.label}</div>
          </button>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="pos-filters">
        <div className="pos-status-pills">
          <button className={`pos-pill${statusFilter === "all" ? " pos-pill-active" : ""}`} onClick={() => setStatusFilter("all")}>All Statuses</button>
          {ALL_STATUSES.map(s => (
            <button key={s} className={`pos-pill${statusFilter === s ? " pos-pill-active" : ""}`} onClick={() => setStatusFilter(s)}>
              {STATUS_CONFIG[s]?.label || s}
            </button>
          ))}
        </div>
        <div className="pos-search-wrap">
          <svg className="pos-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            className="pos-search"
            placeholder="Search company, contact, order #…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button className="pos-search-clear" onClick={() => setSearch("")}>×</button>}
        </div>
      </div>

      {/* ── Grid ── */}
      <div className="pos-grid-wrap">
        <div className="pos-grid-scroll">
          <table className="pos-grid" style={{ minWidth: totalW, width: "100%" }}>
            <colgroup>
              {COLS.map(({ key }) => {
                const wKey = key ?? "__actions";
                return <col key={wKey} style={{ width: colWidths[wKey] }} />;
              })}
            </colgroup>
            <thead>
              <tr>
                {COLS.map(({ key, label }) => {
                  const wKey = key ?? "__actions";
                  const sortable = key && !key.startsWith("__");
                  return (
                    <th
                      key={wKey}
                      className={`pos-th${sortable && sortKey === key ? " pos-th-sorted" : ""}`}
                      onClick={sortable ? () => handleSort(key) : undefined}
                    >
                      <span className="pos-th-inner" style={sortable ? { cursor: "pointer" } : {}}>
                        {label}
                        {sortable && <SortIcon col={key} />}
                      </span>
                      <span
                        className="pos-resize-handle"
                        onMouseDown={e => startResize(e, key)}
                        onClick={e => e.stopPropagation()}
                      />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={COLS.length} className="pos-empty">No records match your filters.</td></tr>
              ) : filtered.map(row => {
                const isExpanded = expandedId === row.id;
                const statusCfg  = STATUS_CONFIG[row.status] || STATUS_CONFIG["Pending"];
                const tUrl       = trackingUrl(row.carrier, row.tracking);
                const itemTotal  = totalItems(row.items);
                const hasNotes   = row.specialInstructions?.length > 2;

                return (
                  <React.Fragment key={row.id}>
                    <tr
                      className={`pos-row${isExpanded ? " pos-row-expanded" : ""}`}
                      onClick={() => setExpandedId(isExpanded ? null : row.id)}
                    >
                      <td className="pos-td pos-td-details" onClick={e => e.stopPropagation()}>
                        <button
                          className={`pos-details-btn${isExpanded ? " pos-details-btn-active" : ""}`}
                          onClick={() => setExpandedId(isExpanded ? null : row.id)}
                        >
                          Details
                        </button>
                      </td>
                      <td className="pos-td pos-td-ordnum">{row.orderNumber}</td>
                      <td className="pos-td" onClick={e => e.stopPropagation()}>
                        {isEdit(row.id, "status")
                          ? posSelect(row.id, "status", ALL_STATUSES)
                          : <span className={`pos-badge ${statusCfg.cls}${canEditShipping ? " pos-editable" : ""}`} onClick={canEditShipping ? e => startEdit(row.id, "status", row.status, e) : undefined}>{statusCfg.label}</span>
                        }
                      </td>
                      <td className="pos-td" onClick={e => e.stopPropagation()}>
                        {posCell(row.id, "shipToCompany", <div className="pos-company">{row.shipToCompany || "—"}</div>, row.shipToCompany)}
                      </td>
                      <td className="pos-td" onClick={e => e.stopPropagation()}>
                        {isEdit(row.id, "shipToContactName")  ? posInput(row.id, "shipToContactName")  : <div className={`pos-contact-name${canEditShipping ? " pos-editable" : ""}`}  onClick={canEditShipping ? e => startEdit(row.id, "shipToContactName",  row.shipToContactName,  e) : undefined}>{row.shipToContactName  || "—"}</div>}
                        {isEdit(row.id, "shipToContactPhone") ? posInput(row.id, "shipToContactPhone") : <div className={`pos-contact-phone${canEditShipping ? " pos-editable" : ""}`} onClick={canEditShipping ? e => startEdit(row.id, "shipToContactPhone", row.shipToContactPhone, e) : undefined}>{row.shipToContactPhone || (canEditShipping ? <span className="pos-editable-empty">+ phone</span> : null)}</div>}
                      </td>
                      <td className="pos-td" onClick={e => e.stopPropagation()}>
                        {posCell(row.id, "orderDate", fmt(row.orderDate), row.orderDate, "date")}
                      </td>
                      <td className="pos-td" onClick={e => e.stopPropagation()}>
                        {posCell(row.id, "requestedShipDate", fmt(row.requestedShipDate), row.requestedShipDate, "date")}
                      </td>
                      <td className="pos-td pos-submitter" onClick={e => e.stopPropagation()}>
                        {posCell(row.id, "submitterName", row.submitterName || "—", row.submitterName)}
                      </td>
                      <td className="pos-td pos-td-qty">{itemTotal > 0 ? itemTotal : "—"}</td>
                      <td className="pos-td" onClick={e => e.stopPropagation()}>
                        {posCell(row.id, "shipDate", fmt(row.shipDate), row.shipDate, "date")}
                      </td>
                      <td className="pos-td pos-td-shipping-cost" onClick={e => e.stopPropagation()}>
                        {isEdit(row.id, "shippingCost")
                          ? posInput(row.id, "shippingCost", "number")
                          : canEditShipping
                            ? <span className="pos-editable" onClick={e => startEdit(row.id, "shippingCost", row.shippingCost ?? "", e)}>
                                {row.shippingCost != null ? `$${parseFloat(row.shippingCost).toFixed(2)}` : <span className="pos-editable-empty">+ cost</span>}
                              </span>
                            : (row.shippingCost != null ? `$${parseFloat(row.shippingCost).toFixed(2)}` : "—")
                        }
                      </td>
                      <td className="pos-td" onClick={e => e.stopPropagation()}>
                        <span className="pos-carrier">{row.carrier || "—"}</span>
                        {row.tracking
                          ? (tUrl
                              ? <a className="pos-tracking-link" href={tUrl} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>{row.tracking}</a>
                              : <span className="pos-tracking">{row.tracking}</span>
                            )
                          : null
                        }
                      </td>
                      <td className="pos-td" onClick={e => e.stopPropagation()}>
                        <div className="pos-row-actions">
                          {hasNotes && (
                            <span className="pos-notes-icon" title={row.specialInstructions}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                              </svg>
                            </span>
                          )}
                          {canEditShipping && (
                            <button
                              className="pos-row-edit-btn"
                              onClick={e => { e.stopPropagation(); setShippingRow(row); }}
                              title="Edit shipping"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                              </svg>
                              Shipping
                            </button>
                          )}
                          {canEditShipping && (
                            confirmDeleteId === row.id ? (
                              <span className="pos-row-delete-confirm">
                                <button className="pos-del-yes" onClick={e => { e.stopPropagation(); handleDelete(row.id); }} disabled={deleting}>{deleting ? "…" : "Yes"}</button>
                                <button className="pos-del-no"  onClick={e => { e.stopPropagation(); setConfirmDeleteId(null); }}>No</button>
                              </span>
                            ) : (
                              <button
                                className="pos-row-delete-btn"
                                onClick={e => { e.stopPropagation(); setConfirmDeleteId(row.id); }}
                                title="Delete order"
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                  <path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                                </svg>
                              </button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="pos-detail-row">
                        <td colSpan={COLS.length}>
                          <div className="pos-detail">

                            {/* Order info */}
                            <div className="pos-detail-section">
                              <div className="pos-detail-label">Order #</div>
                              <div className="pos-detail-value">{row.orderNumber}</div>
                            </div>
                            <div className="pos-detail-section">
                              <div className="pos-detail-label">Ship To Address</div>
                              <div className="pos-detail-value">{row.shipToAddress || "—"}</div>
                            </div>
                            <div className="pos-detail-section">
                              <div className="pos-detail-label">Contact Email</div>
                              <div className="pos-detail-value">{row.shipToContactEmail || "—"}</div>
                            </div>
                            <div className="pos-detail-section">
                              <div className="pos-detail-label">Submitter Email</div>
                              <div className="pos-detail-value">{row.submitterEmail || "—"}</div>
                            </div>
                            {row.estDelivery && (
                              <div className="pos-detail-section">
                                <div className="pos-detail-label">Est. Delivery</div>
                                <div className="pos-detail-value">{fmt(row.estDelivery)}</div>
                              </div>
                            )}

                            {/* POS items breakdown */}
                            <div className="pos-detail-section pos-detail-items">
                              <div className="pos-detail-label">Items Ordered</div>
                              <div className="pos-items-grid">
                                {Object.entries(row.items)
                                  .filter(([, qty]) => qty > 0)
                                  .sort(([a], [b]) => (itemLabelByKey[a] || a).localeCompare(itemLabelByKey[b] || b))
                                  .map(([key, qty]) => (
                                    <div key={key} className="pos-item-row">
                                      <span className="pos-item-label">{itemLabelByKey[key] || key}</span>
                                      <span className="pos-item-qty">{qty}</span>
                                    </div>
                                  ))}
                                {totalItems(row.items) === 0 && <span className="pos-item-none">No items recorded</span>}
                              </div>
                            </div>

                            {hasNotes && (
                              <div className="pos-detail-section pos-detail-notes">
                                <div className="pos-detail-label">Special Instructions</div>
                                <div className="pos-detail-value">{row.specialInstructions}</div>
                              </div>
                            )}

                            {/* Actions */}
                            {(canEditShipping || canEditShipping) && (
                              <div className="pos-detail-section pos-detail-actions">
                                {canEditShipping && (
                                  <button className="pos-edit-shipping-btn" onClick={e => { e.stopPropagation(); setShippingRow(row); }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                                    </svg>
                                    Edit Shipping Details
                                  </button>
                                )}
                                {canEditShipping && (
                                  confirmDeleteId === row.id ? (
                                    <span className="pos-delete-confirm">
                                      <span className="pos-delete-confirm-text">Permanently delete this order?</span>
                                      <button className="pos-delete-confirm-btn" onClick={e => { e.stopPropagation(); handleDelete(row.id); }} disabled={deleting}>
                                        {deleting ? "Deleting…" : "Yes, Delete"}
                                      </button>
                                      <button className="pos-delete-cancel-btn" onClick={e => { e.stopPropagation(); setConfirmDeleteId(null); }} disabled={deleting}>
                                        Cancel
                                      </button>
                                    </span>
                                  ) : (
                                    <button className="pos-delete-btn" onClick={e => { e.stopPropagation(); setConfirmDeleteId(row.id); }}>
                                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                        <path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                                      </svg>
                                      Delete Order
                                    </button>
                                  )
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="pos-grid-footer">
          Showing {filtered.length} of {orders.length} orders
        </div>
      </div>
    </div>

    {successToast && (
      <div className="pos-success-toast">
        Thanks for submitting your order!
      </div>
    )}

    {notifyWarning && (
      <div className="pos-notify-warning" role="alert">
        <span>{notifyWarning}</span>
        <button type="button" onClick={() => setNotifyWarning(null)} aria-label="Dismiss">×</button>
      </div>
    )}

    {showNewModal && (
      <NewPOSModal
        user={user}
        onClose={() => setShowNewModal(false)}
        onCreated={(newOrder) => {
          setOrders(prev => [newOrder, ...prev]);
          setShowNewModal(false);
          setSuccessToast(true);
          setTimeout(() => setSuccessToast(false), 4000);
        }}
      />
    )}

    {shippingRow && (
      <POSShippingModal
        order={shippingRow}
        user={user}
        onNotifyResult={showNotifyWarning}
        onClose={() => setShippingRow(null)}
        onSaved={(updated) => {
          setOrders(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r));
          setShippingRow(null);
        }}
      />
    )}
    </>
  );
}
